import type { ComponentProps, FC, MouseEvent } from "react";
import { useCallback, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/css";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useTransform
} from "framer-motion";
import { useGesture } from "@use-gesture/react";
import { DocumentIcon } from "@9amhealth/shared";
import translate from "src/lib/translate";
import { useBloc } from "src/state/state";
import FileViewerPopupBloc, {
  FileViewerPopupController
} from "./FileViewerPopupBloc";

// ── Constants ──

const SWIPE_DISMISS_THRESHOLD_PX = 120;
const OPACITY_DRAG_RANGE: number[] = [-300, 0, 300];
const OPACITY_OUTPUT_RANGE: number[] = [0.2, 1, 0.2];

const BACKDROP_Z_INDEX = 99999998;
const CONTROLS_Z_INDEX = 99999999;

const TOUCH_TARGET_SIZE = 44;
const GENERIC_DISPLAY_SIZE = 260;
const SPINNER_SIZE = 36;
const ICON_SIZE = 22;
const GENERIC_ICON_SIZE = 48;

const TRANSITION_FAST = { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const };

const MAX_IMAGE_ZOOM = 4;
const DOUBLE_TAP_ZOOM = 2;
const DOUBLE_TAP_DELAY_MS = 300;
const SNAP_SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };

const CAN_SHARE =
  typeof navigator !== "undefined" && typeof navigator.share === "function";

// ── Icons ──

const ShareIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M7 11c-.93 0-1.395 0-1.776.102a3 3 0 0 0-2.122 2.121C3 13.606 3 14.07 3 15v1.2c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C5.28 21 6.12 21 7.8 21h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 18.72 21 17.88 21 16.2V15c0-.93 0-1.395-.102-1.777a3 3 0 0 0-2.122-2.12C18.396 11 17.93 11 17 11m-1-4-4-4m0 0L8 7m4-4v12"
    />
  </svg>
);

const CloseIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ── Styled components ──

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: ${BACKDROP_Z_INDEX};
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
`;

const ContentWrapper = styled(motion.div)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 6rem);
`;

const ZoomLayer = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
`;

const PreviewImage = styled.img`
  display: block;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 6rem);
  object-fit: contain;
  border-radius: 4px;
  user-select: none;
  -webkit-user-drag: none;
`;

const GenericDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: ${GENERIC_DISPLAY_SIZE}px;
  height: ${GENERIC_DISPLAY_SIZE}px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  color: white;
  text-align: center;
  padding: 1.5rem;

  svg {
    width: ${GENERIC_ICON_SIZE}px;
    height: ${GENERIC_ICON_SIZE}px;
    opacity: 0.6;
  }
`;

const GenericFileName = styled.div`
  font-weight: 600;
  font-size: 0.9375rem;
  word-break: break-word;
  line-height: 1.3;
`;

const GenericMeta = styled.div`
  font-size: 0.8125rem;
  opacity: 0.7;
`;

const GenericNotice = styled.div`
  font-size: 0.8125rem;
  opacity: 0.5;
  font-style: italic;
`;

const Spinner = styled.div`
  width: ${SPINNER_SIZE}px;
  height: ${SPINNER_SIZE}px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const OverlayIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${TOUCH_TARGET_SIZE}px;
  height: ${TOUCH_TARGET_SIZE}px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: white;
  cursor: pointer;
  transition: all 0.15s ease-out;
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) {
    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }
  }

  &:active {
    transform: scale(0.94);
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: ${ICON_SIZE}px;
    height: ${ICON_SIZE}px;
  }
`;

const ActionBar = styled.div`
  position: fixed;
  bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  left: 1rem;
  display: flex;
  gap: 0.25rem;
  z-index: ${CONTROLS_Z_INDEX};
`;

const ActionButton = styled(OverlayIconButton)`
  border-radius: 12px;
`;

const CloseButton = styled(OverlayIconButton)`
  position: fixed;
  top: calc(1rem + env(safe-area-inset-top, 0px));
  right: 1rem;
  z-index: ${CONTROLS_Z_INDEX};
  border-radius: 50%;
`;

// ── Stable handlers for module-level singleton ──

const handleClose = (): void => {
  FileViewerPopupController.close();
};

const handleShare = (): void => {
  void FileViewerPopupController.share();
};

// ── Component ──

const FileViewerPopup: FC = () => {
  const [state] = useBloc(FileViewerPopupBloc, {
    create: () => FileViewerPopupController
  });

  const {
    isOpen,
    previewUrl,
    loading,
    isPreviewable,
    filename,
    fileTypeLabel,
    fileSizeLabel
  } = state;

  const backdropRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef(1);
  const panOffsetRef = useRef({ x: 0, y: 0 });
  const lastTapRef = useRef(0);

  const dismissY = useMotionValue(0);
  const contentOpacity = useTransform(
    dismissY,
    OPACITY_DRAG_RANGE,
    OPACITY_OUTPUT_RANGE
  );

  const zoomScale = useMotionValue(1);
  const panX = useMotionValue(0);
  const panY = useMotionValue(0);

  const resetTransforms = useCallback(() => {
    zoomScale.set(1);
    panX.set(0);
    panY.set(0);
    dismissY.set(0);
    scaleRef.current = 1;
    panOffsetRef.current = { x: 0, y: 0 };
  }, [zoomScale, panX, panY, dismissY]);

  useEffect(() => {
    if (!isOpen) {
      resetTransforms();
    }
  }, [isOpen, resetTransforms]);

  const handleBackdropClick = useCallback((e: MouseEvent) => {
    if (e.target === backdropRef.current) {
      handleClose();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  const bind = useGesture(
    {
      onDrag: ({ movement: [mx, my], memo }) => {
        const mode = memo ?? (scaleRef.current > 1 ? "pan" : "dismiss");
        if (mode === "pan") {
          panX.set(panOffsetRef.current.x + mx);
          panY.set(panOffsetRef.current.y + my);
        } else {
          dismissY.set(my);
        }
        return mode;
      },
      onDragEnd: ({ movement: [mx, my], memo }) => {
        if (memo === "pan") {
          panOffsetRef.current.x += mx;
          panOffsetRef.current.y += my;
        } else {
          if (Math.abs(my) > SWIPE_DISMISS_THRESHOLD_PX) {
            handleClose();
          } else {
            animate(dismissY, 0, SNAP_SPRING);
          }
        }
      },
      onPinch: ({ offset: [s] }) => {
        zoomScale.set(s);
        scaleRef.current = s;
      },
      onPinchEnd: () => {
        if (scaleRef.current < 1) {
          animate(zoomScale, 1, SNAP_SPRING);
          animate(panX, 0, SNAP_SPRING);
          animate(panY, 0, SNAP_SPRING);
          scaleRef.current = 1;
          panOffsetRef.current = { x: 0, y: 0 };
        }
      }
    },
    {
      drag: { filterTaps: true },
      pinch: {
        scaleBounds: { min: 0.5, max: MAX_IMAGE_ZOOM },
        from: () => [zoomScale.get(), 0]
      }
    }
  );

  const handleDoubleTap = useCallback(() => {
    const now = performance.now();
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY_MS) {
      lastTapRef.current = 0;
      if (scaleRef.current > 1) {
        animate(zoomScale, 1, SNAP_SPRING);
        animate(panX, 0, SNAP_SPRING);
        animate(panY, 0, SNAP_SPRING);
        scaleRef.current = 1;
        panOffsetRef.current = { x: 0, y: 0 };
      } else {
        animate(zoomScale, DOUBLE_TAP_ZOOM, SNAP_SPRING);
        scaleRef.current = DOUBLE_TAP_ZOOM;
      }
    } else {
      lastTapRef.current = now;
    }
  }, [zoomScale, panX, panY]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Backdrop
            ref={backdropRef}
            onClick={handleBackdropClick}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={TRANSITION_FAST}
            aria-modal="true"
            role="dialog"
            aria-label={filename ?? "File viewer"}
          >
            <ContentWrapper
              style={{ y: dismissY, opacity: contentOpacity }}
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={TRANSITION_FAST}
            >
              <ZoomLayer
                {...(bind() as ComponentProps<typeof ZoomLayer>)}
                onClick={handleDoubleTap}
                style={{ x: panX, y: panY, scale: zoomScale }}
              >
                {loading && <Spinner />}

                {!loading && isPreviewable && previewUrl && (
                  <PreviewImage
                    src={previewUrl}
                    alt={filename ?? ""}
                    draggable={false}
                  />
                )}

                {!loading && !isPreviewable && (
                  <GenericDisplay>
                    <DocumentIcon />
                    {filename && <GenericFileName>{filename}</GenericFileName>}
                    <GenericMeta>
                      {[fileTypeLabel, fileSizeLabel]
                        .filter(Boolean)
                        .join(" · ")}
                    </GenericMeta>
                    <GenericNotice>
                      {translate("fileViewer.previewNotAvailable")}
                    </GenericNotice>
                  </GenericDisplay>
                )}
              </ZoomLayer>
            </ContentWrapper>
          </Backdrop>

          <CloseButton onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </CloseButton>

          <ActionBar>
            {CAN_SHARE && (
              <ActionButton onClick={handleShare} aria-label="Share">
                <ShareIcon />
              </ActionButton>
            )}
          </ActionBar>
        </>
      )}
    </AnimatePresence>
  );
};

export default FileViewerPopup;
